import { useState } from 'react';

interface CommunityAuthModalProps {
  onClose: () => void;
  onSuccess: (user: { name: string; role: string; email: string }) => void;
}

type AuthMode = 'login' | 'register';
type LoginMethod = 'email' | 'phone';

export default function CommunityAuthModal({ onClose, onSuccess }: CommunityAuthModalProps) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email');
  const [role, setRole] = useState<'student' | 'teacher' | 'parent'>('student');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    grade: '',
    region: '',
    subjects: '',
    childGrade: '',
    verifyCode: '',
  });
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);

  const sendCode = () => {
    if (countdown > 0) return;
    setCodeSent(true);
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      const identifier = loginMethod === 'email' ? form.email : form.phone;
      onSuccess({
        name: form.name || identifier.split('@')[0] || '用户',
        role,
        email: form.email || form.phone,
      });
    }, 1000);
  };

  const roleOptions = [
    { value: 'student', label: '学生', icon: 'ri-graduation-cap-line', desc: '寻找学伴、求助辅导' },
    { value: 'teacher', label: '教师', icon: 'ri-user-star-line', desc: '分享知识、答疑解惑' },
    { value: 'parent', label: '家长', icon: 'ri-parent-line', desc: '为孩子找学伴' },
  ] as const;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-5 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white"></i>
          </button>
          <h2 className="text-xl font-black mb-1">
            {mode === 'login' ? '登录社区' : '加入互助社区'}
          </h2>
          <p className="text-white/80 text-sm">城乡学生互助，师生共同成长</p>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Mode Toggle */}
          <div className="flex gap-1 bg-gray-100 rounded-full p-1 mb-5">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === 'login' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
            >
              登录
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === 'register' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
            >
              注册
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Register: Role Selection */}
            {mode === 'register' && (
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-2 block">我的身份</label>
                <div className="grid grid-cols-3 gap-2">
                  {roleOptions.map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setRole(r.value)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all cursor-pointer ${
                        role === r.value ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full ${role === r.value ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <i className={`${r.icon} text-sm ${role === r.value ? 'text-green-600' : 'text-gray-500'}`}></i>
                      </div>
                      <span className={`text-xs font-semibold ${role === r.value ? 'text-green-700' : 'text-gray-600'}`}>{r.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Register: Name */}
            {mode === 'register' && (
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">姓名/昵称</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="请输入你的姓名或昵称"
                  className="w-full bg-gray-50 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-200 border border-transparent focus:border-green-300"
                />
              </div>
            )}

            {/* Login Method Toggle */}
            <div>
              <div className="flex gap-3 mb-2">
                <button
                  type="button"
                  onClick={() => setLoginMethod('email')}
                  className={`text-xs font-semibold pb-1 border-b-2 transition-all cursor-pointer ${loginMethod === 'email' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-400'}`}
                >
                  邮箱登录
                </button>
                <button
                  type="button"
                  onClick={() => setLoginMethod('phone')}
                  className={`text-xs font-semibold pb-1 border-b-2 transition-all cursor-pointer ${loginMethod === 'phone' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-400'}`}
                >
                  手机号登录
                </button>
              </div>

              {loginMethod === 'email' ? (
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="请输入邮箱地址"
                  className="w-full bg-gray-50 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-200 border border-transparent focus:border-green-300"
                />
              ) : (
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="请输入手机号码"
                  className="w-full bg-gray-50 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-200 border border-transparent focus:border-green-300"
                />
              )}
            </div>

            {/* Password or Verify Code */}
            {loginMethod === 'email' ? (
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">密码</label>
                <input
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={mode === 'register' ? '设置密码（至少6位）' : '请输入密码'}
                  className="w-full bg-gray-50 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-200 border border-transparent focus:border-green-300"
                />
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">验证码</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    value={form.verifyCode}
                    onChange={(e) => setForm({ ...form, verifyCode: e.target.value })}
                    placeholder="请输入验证码"
                    className="flex-1 bg-gray-50 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-200 border border-transparent focus:border-green-300"
                  />
                  <button
                    type="button"
                    onClick={sendCode}
                    disabled={countdown > 0}
                    className={`px-4 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      countdown > 0 ? 'bg-gray-100 text-gray-400' : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {countdown > 0 ? `${countdown}s` : codeSent ? '重新发送' : '发送验证码'}
                  </button>
                </div>
              </div>
            )}

            {/* Register: Extra Fields */}
            {mode === 'register' && role === 'student' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">年级</label>
                  <select
                    value={form.grade}
                    onChange={(e) => setForm({ ...form, grade: e.target.value })}
                    className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none cursor-pointer border border-transparent"
                  >
                    <option value="">选择年级</option>
                    {['小学', '初中', '高中', '大学', '考研'].map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">地区类型</label>
                  <select
                    value={form.region}
                    onChange={(e) => setForm({ ...form, region: e.target.value })}
                    className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none cursor-pointer border border-transparent"
                  >
                    <option value="">城市/农村</option>
                    <option value="urban">城市学生</option>
                    <option value="rural">农村学生</option>
                  </select>
                </div>
              </div>
            )}

            {mode === 'register' && role === 'teacher' && (
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">教学科目</label>
                <input
                  type="text"
                  value={form.subjects}
                  onChange={(e) => setForm({ ...form, subjects: e.target.value })}
                  placeholder="如：数学、英语（用逗号分隔）"
                  className="w-full bg-gray-50 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-200 border border-transparent focus:border-green-300"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-green-600 text-white font-bold rounded-full cursor-pointer hover:bg-green-700 transition-all whitespace-nowrap disabled:opacity-60"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> 处理中...
                </span>
              ) : mode === 'login' ? '登录' : '立即注册，加入社区'}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-4">
            注册即代表同意
            <span className="text-green-600 cursor-pointer hover:underline mx-1">用户协议</span>
            和
            <span className="text-green-600 cursor-pointer hover:underline mx-1">隐私政策</span>
          </p>
        </div>
      </div>
    </div>
  );
}
